import React, { useState, useEffect, useRef } from 'react';
import './PaymentModal.css';
import AccessCodeDisplay from './components/AccessCodeDisplay';
import { getAppConfig, DEFAULT_APP } from './constants/mobileApps';

// Stripe publishable key
const STRIPE_PUBLISHABLE_KEY = 'pk_live_51RZ9bxFJrgqo7h8V541vPE0R51TbLl1vQvUxzICb65qwOJhKOLCUVdNE8QoiF3jAuGPwUAKm3Iffwd3hj4605d4A00OF2xKUU8';

const PaymentModal = ({ isOpen, onClose, plan, profileSlug, profileData }) => {
    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [cardError, setCardError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [accessCode, setAccessCode] = useState('');
    const [stripe, setStripe] = useState(null);
    const [cardElement, setCardElement] = useState(null);
    const cardElementRef = useRef(null);

    // Voucher state
    const [showVoucherInput, setShowVoucherInput] = useState(false);
    const [voucherCode, setVoucherCode] = useState('');
    const [voucherValidating, setVoucherValidating] = useState(false);
    const [voucherError, setVoucherError] = useState('');
    const [appliedVoucher, setAppliedVoucher] = useState(null);

    // Payment method state
    const insuranceEnabled = profileData?.paymentOptions?.insuranceEnabled === true;
    const vouchersEnabled = profileData?.paymentOptions?.vouchersEnabled !== false;
    const acceptedNetworks = profileData?.paymentOptions?.acceptedNetworks || [];
    const [paymentMethod, setPaymentMethod] = useState('card');

    // Insurance state
    const [insuranceNetwork, setInsuranceNetwork] = useState('');
    const [policyNumber, setPolicyNumber] = useState('');
    const [memberId, setMemberId] = useState('');
    const [insuranceError, setInsuranceError] = useState('');
    const [insuranceSuccess, setInsuranceSuccess] = useState(false);

    useEffect(() => {
        if (typeof window.Stripe !== 'undefined' && !stripe) {
            const stripeInstance = window.Stripe(STRIPE_PUBLISHABLE_KEY);
            setStripe(stripeInstance);
        }
    }, [stripe]);

    useEffect(() => {
        if (stripe && isOpen && !cardElement && cardElementRef.current) {
            const elements = stripe.elements();
            const card = elements.create('card', {
                style: {
                    base: {
                        fontSize: '16px',
                        color: '#000000e6',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
                        '::placeholder': {
                            color: '#00000099',
                        },
                    },
                    invalid: {
                        color: '#dc3545',
                    },
                },
            });

            card.mount(cardElementRef.current);
            card.on('change', (event) => {
                if (event.error) {
                    setCardError(event.error.message);
                } else {
                    setCardError('');
                }
            });

            setCardElement(card);
        }

        return () => {
            if (cardElement && !isOpen) {
                cardElement.unmount();
                setCardElement(null);
            }
        };
    }, [stripe, isOpen, cardElement]);

    // Generate access code (XXX-XXX format)
    const generateAccessCode = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        let part1 = '';
        let part2 = '';
        for (let i = 0; i < 3; i++) {
            part1 += chars.charAt(Math.floor(Math.random() * chars.length));
            part2 += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return `${part1}-${part2}`;
    };

    // Validate voucher code
    const validateVoucher = async () => {
        if (!voucherCode.trim()) {
            setVoucherError('Please enter a voucher code');
            return;
        }

        setVoucherValidating(true);
        setVoucherError('');

        try {
            const response = await fetch(`/api/get-code?type=payment-voucher&key=${encodeURIComponent(voucherCode.trim())}`);
            const result = await response.json();

            if (!result.found) {
                setVoucherError('Voucher code not found');
                setAppliedVoucher(null);
                return;
            }

            if (!result.valid) {
                setVoucherError(result.reason || 'Voucher is not valid');
                setAppliedVoucher(null);
                return;
            }

            // Check if voucher applies to this profile
            if (result.appliesTo && result.appliesTo !== 'all' && result.appliesTo !== profileSlug) {
                setVoucherError('This voucher cannot be used for this provider');
                setAppliedVoucher(null);
                return;
            }

            // Voucher is valid
            setAppliedVoucher({
                code: voucherCode.trim(),
                stripeCouponId: result.stripeCouponId,
                discountType: result.discountType,
                discountValue: result.discountValue
            });
            setVoucherError('');
        } catch (err) {
            console.error('Voucher validation error:', err);
            setVoucherError('Failed to validate voucher');
            setAppliedVoucher(null);
        } finally {
            setVoucherValidating(false);
        }
    };

    // Remove applied voucher
    const removeVoucher = () => {
        setAppliedVoucher(null);
        setVoucherCode('');
        setVoucherError('');
    };

    // Calculate discounted total
    const calculateDiscountedTotal = () => {
        const baseTotal = plan?.total || plan?.price || 0;
        if (!appliedVoucher) return baseTotal;

        if (appliedVoucher.discountType === 'percent') {
            return Math.round(baseTotal * (1 - appliedVoucher.discountValue / 100));
        } else if (appliedVoucher.discountType === 'fixed') {
            return Math.max(0, baseTotal - appliedVoucher.discountValue);
        } else if (appliedVoucher.discountType === 'free') {
            return 0;
        }
        return baseTotal;
    };

    // Handle insurance submission
    const handleInsuranceSubmit = async (e) => {
        e.preventDefault();
        setInsuranceError('');
        setIsProcessing(true);

        try {
            const response = await fetch('/api/submit-inquiry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    slug: profileSlug,
                    customerName,
                    customerEmail,
                    insuranceNetwork,
                    policyNumber,
                    memberId,
                    planId: plan?.id,
                    planName: plan?.name,
                    duration: plan?.selectedDuration || 1,
                    amount: plan?.total || plan?.price || 0,
                    currency: plan?.currency || 'GBP'
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to submit request. Please try again.');
            }

            setInsuranceSuccess(true);
            setShowSuccess(true);
        } catch (error) {
            console.error('Insurance submission error:', error);
            setInsuranceError(error.message || 'An error occurred. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !cardElement) {
            setCardError('Stripe has not loaded yet');
            return;
        }

        if (!plan || !profileSlug) {
            setCardError('Missing plan or profile information');
            return;
        }

        setCardError('');
        setIsProcessing(true);

        // REQ-050 Phase 1B: app-passed patientId (stashed by pre-takeover script
        // in public/index.html when bio page was opened with ?pid=...).
        // Gated on profileSlug so a stashed pid from a prior /profile/* visit in
        // the same tab can't leak into payments made on /solutions/* (where
        // PaymentModal is also mounted with profileSlug={null}).
        const patientId = (profileSlug && typeof window !== 'undefined' && window.sessionStorage)
            ? window.sessionStorage.getItem('pa_pid')
            : null;

        try {
            // Generate access code upfront
            const generatedAccessCode = generateAccessCode();

            // Step 1: Create payment intent via website API
            const response = await fetch('/api/create-payment-intent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    profileSlug: profileSlug,
                    planId: plan.id,
                    customerEmail: customerEmail,
                    customerName: customerName,
                    duration: plan.selectedDuration || 1,
                    amount: calculateDiscountedTotal(),
                    stripeCouponId: appliedVoucher?.stripeCouponId || null
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to create payment. Please try again.');
            }

            const data = await response.json();
            const clientSecret = data.clientSecret;
            const paymentIntentId = data.paymentIntentId;

            if (!clientSecret) {
                throw new Error('Invalid response from payment server.');
            }

            // Step 2: Confirm payment with Stripe
            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: {
                        name: customerName,
                        email: customerEmail,
                    },
                },
            });

            if (error) {
                throw error;
            } else if (paymentIntent.status === 'succeeded') {
                // Step 3: Complete payment via unified transaction API
                const completeResponse = await fetch('/api/complete-transaction', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        type: 'payment',
                        slug: profileSlug,
                        paymentIntentId: paymentIntentId,
                        customerName: customerName,
                        customerEmail: customerEmail,
                        accessCode: generatedAccessCode,
                        voucherCode: appliedVoucher?.code || null,
                        voucherDiscount: appliedVoucher ? (plan.total || plan.price) - calculateDiscountedTotal() : 0,
                        patientId: patientId || undefined
                    })
                });

                if (!completeResponse.ok) {
                    // Payment succeeded but completion failed - still show success
                    console.error('Error completing payment:', await completeResponse.text());
                    setAccessCode(generatedAccessCode);
                } else {
                    const completeData = await completeResponse.json();
                    setAccessCode(completeData.accessCode || generatedAccessCode);

                    // REQ-050 Phase 1B: if user came from the app (pid in sessionStorage),
                    // hand control back via the connect.physioactivity.com App Link so the
                    // app's MainActivity.handleDeepLink can show its confirmation screen +
                    // start its verifying-subscription poll. Uses PaymentIntent ID as the
                    // opaque session_id substitute (PODecision 2026-05-09_req-050-stripe-
                    // payment-intent-as-session-id). On success the app handles the rest;
                    // we stop here so the access-code success screen doesn't briefly show.
                    if (patientId && completeResponse.ok) {
                        const appName = profileData?.mobileApp?.appId || DEFAULT_APP;
                        const returnUrl = 'https://connect.physioactivity.com/'
                            + encodeURIComponent(appName)
                            + '/subscription/complete'
                            + '?session_id=' + encodeURIComponent(paymentIntentId)
                            + '&pid=' + encodeURIComponent(patientId);
                        try { window.sessionStorage.removeItem('pa_pid'); } catch (e) {}
                        window.location.href = returnUrl;
                        return;
                    }
                }

                // Show success message
                setShowSuccess(true);

                // Reset form
                setCustomerName('');
                setCustomerEmail('');
                setVoucherCode('');
                setAppliedVoucher(null);
                cardElement.clear();
            }
        } catch (error) {
            console.error('Payment error:', error);
            setCardError(error.message || 'An error occurred processing your payment. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        setShowSuccess(false);
        setCustomerName('');
        setCustomerEmail('');
        setCardError('');
        setVoucherCode('');
        setVoucherError('');
        setAppliedVoucher(null);
        setShowVoucherInput(false);
        setPaymentMethod('card');
        setInsuranceNetwork('');
        setPolicyNumber('');
        setMemberId('');
        setInsuranceError('');
        setInsuranceSuccess(false);
        if (cardElement) {
            cardElement.clear();
        }
        onClose();
    };

    if (!isOpen) return null;

    // Get currency symbol
    const getCurrencySymbol = (currency) => {
        const symbols = { GBP: '£', USD: '$', EUR: '€' };
        return symbols[currency?.toUpperCase()] || currency || '£';
    };

    const planName = plan?.name || 'Plan';
    const planTotal = plan?.total || plan?.price || 0;
    const planMonthly = plan?.monthly || plan?.price || 0;
    const planDuration = plan?.selectedDuration || 1;
    const planCurrency = plan?.currency || 'GBP';
    const providerName = profileData?.displayName || 'your provider';

    return (
        <div className="payment-modal-overlay" onClick={handleClose}>
            <div className="payment-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="payment-modal-header">
                    <h2 className="payment-modal-title">{showSuccess ? 'Booking Confirmed!' : planName}</h2>
                    <button className="payment-modal-close" onClick={handleClose}>&times;</button>
                </div>

                {!showSuccess ? (
                    <form onSubmit={paymentMethod === 'insurance' ? handleInsuranceSubmit : handleSubmit} className="payment-form">
                        <div className="form-group">
                            <label className="form-label">Full Name</label>
                            <input
                                type="text"
                                className="form-input"
                                value={customerName}
                                onChange={(e) => setCustomerName(e.target.value)}
                                required
                                placeholder="John Doe"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Email Address</label>
                            <input
                                type="email"
                                className="form-input"
                                value={customerEmail}
                                onChange={(e) => setCustomerEmail(e.target.value)}
                                required
                                placeholder="john@example.com"
                            />
                        </div>

                        {/* Payment Method Toggle - only when insurance is available */}
                        {insuranceEnabled && (
                            <div className="form-group">
                                <label className="form-label">Payment Method</label>
                                <div className="payment-method-toggle">
                                    <button
                                        type="button"
                                        className={`payment-method-btn ${paymentMethod === 'card' ? 'active' : ''}`}
                                        onClick={() => setPaymentMethod('card')}
                                    >
                                        Card Payment
                                    </button>
                                    <button
                                        type="button"
                                        className={`payment-method-btn ${paymentMethod === 'insurance' ? 'active' : ''}`}
                                        onClick={() => setPaymentMethod('insurance')}
                                    >
                                        Insurance
                                    </button>
                                </div>
                            </div>
                        )}

                        {paymentMethod === 'card' ? (
                            <>
                                {/* Voucher Code Section */}
                                {vouchersEnabled && (
                                    <div className="form-group">
                                        {!showVoucherInput && !appliedVoucher ? (
                                            <button
                                                type="button"
                                                className="voucher-toggle"
                                                onClick={() => setShowVoucherInput(true)}
                                            >
                                                Have a voucher code?
                                            </button>
                                        ) : appliedVoucher ? (
                                            <div className="voucher-applied">
                                                <span className="voucher-applied-label">Voucher applied:</span>
                                                <span className="voucher-applied-code">{appliedVoucher.code}</span>
                                                <span className="voucher-applied-discount">
                                                    {appliedVoucher.discountType === 'percent' && `-${appliedVoucher.discountValue}%`}
                                                    {appliedVoucher.discountType === 'fixed' && `-${getCurrencySymbol(planCurrency)}${appliedVoucher.discountValue}`}
                                                    {appliedVoucher.discountType === 'free' && 'FREE'}
                                                </span>
                                                <button type="button" className="voucher-remove" onClick={removeVoucher}>Remove</button>
                                            </div>
                                        ) : (
                                            <div className="voucher-input-container">
                                                <label className="form-label">Voucher Code</label>
                                                <div className="voucher-input-row">
                                                    <input
                                                        type="text"
                                                        className="form-input voucher-input"
                                                        value={voucherCode}
                                                        onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                                                        placeholder="Enter code"
                                                        disabled={voucherValidating}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="voucher-apply-btn"
                                                        onClick={validateVoucher}
                                                        disabled={voucherValidating || !voucherCode.trim()}
                                                    >
                                                        {voucherValidating ? '...' : 'Apply'}
                                                    </button>
                                                </div>
                                                {voucherError && <div className="voucher-error">{voucherError}</div>}
                                                <button
                                                    type="button"
                                                    className="voucher-cancel"
                                                    onClick={() => { setShowVoucherInput(false); setVoucherCode(''); setVoucherError(''); }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="form-group">
                                    <label className="form-label">Card Details</label>
                                    <div ref={cardElementRef} className="card-element-container"></div>
                                    {cardError && <div className="card-error">{cardError}</div>}
                                </div>

                                <div className="form-group">
                                    <p className="payment-total">
                                        {appliedVoucher ? (
                                            <>
                                                Total: <span className="original-price">{getCurrencySymbol(planCurrency)}{planTotal}</span>
                                                {' '}<strong>{getCurrencySymbol(planCurrency)}{calculateDiscountedTotal()}</strong>
                                            </>
                                        ) : (
                                            <>Total: <strong>{getCurrencySymbol(planCurrency)}{planTotal}</strong></>
                                        )}
                                        <span className="payment-period"> for {planDuration} month{planDuration > 1 ? 's' : ''}</span>
                                    </p>
                                    {planDuration > 1 && !appliedVoucher && (
                                        <p className="payment-monthly">({getCurrencySymbol(planCurrency)}{planMonthly}/month)</p>
                                    )}
                                    <p className="payment-secure">Secure payment powered by Stripe</p>
                                </div>

                                <button
                                    type="submit"
                                    className="payment-submit-btn"
                                    disabled={isProcessing || !stripe}
                                >
                                    {isProcessing ? 'Processing...' : 'Complete Payment'}
                                </button>
                            </>
                        ) : (
                            <>
                                {/* Insurance Form */}
                                <div className="form-group">
                                    <label className="form-label">Insurance Provider</label>
                                    <select
                                        className="form-input"
                                        value={insuranceNetwork}
                                        onChange={(e) => setInsuranceNetwork(e.target.value)}
                                        required
                                    >
                                        <option value="">Select your insurer</option>
                                        {acceptedNetworks.map(network => (
                                            <option key={network} value={network}>
                                                {network.charAt(0).toUpperCase() + network.slice(1)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Policy Number</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={policyNumber}
                                        onChange={(e) => setPolicyNumber(e.target.value)}
                                        required
                                        placeholder="e.g. BUP123456"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Member ID</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={memberId}
                                        onChange={(e) => setMemberId(e.target.value)}
                                        placeholder="e.g. MEM789 (optional)"
                                    />
                                </div>

                                <div className="form-group">
                                    <p className="payment-total">
                                        Plan: <strong>{planName}</strong>
                                        <span className="payment-period"> for {planDuration} month{planDuration > 1 ? 's' : ''}</span>
                                    </p>
                                    <p className="insurance-note">
                                        Your insurance details will be verified by {providerName}. You'll receive a confirmation email once your coverage has been approved.
                                    </p>
                                </div>

                                {insuranceError && <div className="card-error" style={{ marginBottom: '12px' }}>{insuranceError}</div>}

                                <button
                                    type="submit"
                                    className="payment-submit-btn"
                                    disabled={isProcessing || !insuranceNetwork || !policyNumber}
                                >
                                    {isProcessing ? 'Submitting...' : 'Submit Insurance Request'}
                                </button>
                            </>
                        )}
                    </form>
                ) : (
                    <div className="payment-success">
                        {insuranceSuccess ? (
                            <>
                                <div className="success-message">
                                    <strong>Insurance Request Submitted</strong>
                                    <p>Your request for the {planName} plan has been sent to {providerName}.</p>
                                </div>
                                <p className="access-code-instructions">
                                    {providerName} will verify your insurance coverage and get back to you at <strong>{customerEmail}</strong> within 24-48 hours. Once approved, you'll receive your access code by email.
                                </p>
                                <button className="payment-close-btn" onClick={handleClose}>Close</button>
                            </>
                        ) : (
                            <>
                                <div className="success-message">
                                    <strong>Booking Confirmed!</strong>
                                    <p>Your {planName} has been activated.</p>
                                </div>
                                <AccessCodeDisplay
                                    accessCode={accessCode}
                                    appId={profileData?.mobileApp?.appId || DEFAULT_APP}
                                    variant="default"
                                />
                                <p className="access-code-instructions">
                                    This code has also been sent to your email. Scan the QR code or enter the code manually in the {getAppConfig(profileData?.mobileApp?.appId || DEFAULT_APP).name} app to access your program with {providerName}.
                                </p>
                                <button className="payment-close-btn" onClick={handleClose}>Close</button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentModal;
